/* eslint-disable no-unused-vars */
/* eslint-disable quotes */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import ApiService from '../../../services/ApiService';
import {
    TextField, MenuItem, Button, Grid, Typography, Box, Paper,
    CircularProgress, Alert, Divider, Autocomplete, FormHelperText,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, Checkbox, FormControlLabel,
    Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircuitSelection from '../../../components/CircuitSelection';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const PRODUCT_OPTIONS = ['DIA (Dedicated Internet Access)', 'Broadband'];
const HANDOFF_OPTIONS = ['Copper GigE', 'Copper FastE', 'Fiber MM', 'Fiber SM'];
const LAN_PORT_OPTIONS = ['One Lan Port', 'Two Lan Port'];
const IMPLEMENTATION_OPTIONS = ['Parallel Installation', 'Existing Movement', 'Not Applicable'];
const DIVERSITY_OPTIONS = ['None', 'Path Diversity Required', 'Full Diversity Required'];
const YES_NO = ['Yes', 'No'];
const SITE_TYPE_OPTIONS = ['Normal Site', 'Call Center', 'Data Center'];
const EXPEDITE_OPTIONS = ['BAU', 'Expedite'];
const REGION_OPTIONS = [
    'APAC', 'EU1', 'EU2', 'EU3', 'France', 'Italy', 'US', 'Austria', 'CEE', 'IberoLatam'
];

// TEM country code -> Region, per "region and country mapping.xlsx" (Global WAN Provider Services table).
// Codes not listed here have no defined region; the user picks manually.
const COUNTRY_TO_REGION = {
    // APAC (Telstra)
    IDN: 'APAC', JPN: 'APAC', TWN: 'APAC', CHN: 'APAC', CHF: 'APAC', CHG: 'APAC',
    ARE: 'APAC', IND: 'APAC', PHL: 'APAC', SRI: 'APAC',
    AUS: 'APAC', HKG: 'APAC', KOR: 'APAC', MAL: 'APAC', MYS: 'APAC', NZL: 'APAC',
    SGP: 'APAC', THA: 'APAC', RUN: 'APAC',
    MOR: 'APAC', ZAF: 'APAC',
    // CEE (DTBS)
    BGR: 'CEE', HRV: 'CEE', CZE: 'CEE', GRC: 'CEE', HUN: 'CEE', LTU: 'CEE',
    POL: 'CEE', ROU: 'CEE', SVK: 'CEE', TUR: 'CEE',
    // EU1 (A1)
    AUT: 'EU1',
    // France (SFR, Orange)
    FRA: 'France', ALZ: 'France', MUS: 'France',
    // EU2 (COLT)
    DEU: 'EU2', CHE: 'EU2',
    // EU3 (BT)
    GBR: 'EU3', IRL: 'EU3', BEL: 'EU3', NLD: 'EU3', LUX: 'EU3',
    DNK: 'EU3', FIN: 'EU3', LIE: 'EU3', NOR: 'EU3', SWE: 'EU3',
    // Italy (Fastweb)
    ITA: 'Italy',
    // IberoLatam (Telefonica)
    ESP: 'IberoLatam', PRT: 'IberoLatam', MEX: 'IberoLatam',
    // USA (LUMEN, BT, COLT)
    USA: 'US', CAN: 'US',
};

const PHONE_REGEX = /^\+[\d\s()./-]{7,20}$/;

const validatePhone = (value) => {
    if (!value) return 'Phone number is required';
    if (!PHONE_REGEX.test(value.trim())) return 'Format: +XX (XXX) XXX-XXXX';
    return null;
};

const validateEmail = (value) => {
    if (!value) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address';
    return null;
};

const getMinRequiredDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return d.toISOString().slice(0, 10);
};

const validateRequiredDate = (value) => {
    if (!value) return null;
    if (value < getMinRequiredDate()) return 'Must be at least 3 months from today';
    return null;
};

// Consistent field height: every field always renders a helper text line
// so rows never shift when some fields have errors/hints and others don't.
const FIELD_SX = {
    '& .MuiFormHelperText-root': { minHeight: '1.25em' },
};

const CircuitOrderForm = ({
    control,
    watch,
    setValue,
    getValues,
    trigger,
    handleSubmit,
    userRole,
    isUpdateMode,
    updateId,
    externalDemandData,
}) => {
    const navigate = useNavigate();
    const { accounts } = useMsal();
    const userName = accounts[0]?.name || '';

    const [localData, setLocalData] = useState({
        oe_name: '',
        cost_centre: '',
        debtor: '',
        product: '',
        region: '',
        customer_required_date: '',
        is_new_site: 'No',
        site_country: '',
        site_city: '',
        site_type: 'Normal Site',
        request_description: '',
        business_reasons: '',
        customer_site_id: '',
        location_id: '',
        existing_circuit_ids: '',
        install_site_address: '',
        demarcation_location: '',
        dual_building_entry_availability: 'No',
        circuit_id: '',
        circuit_id_2: '',
        local_provider_name: '',
        handoff_interface_type: '',
        lan_port_requirement: '',
        port_speed_mbps: '',
        access_speed_mbps: '',
        access_speed_mbps_2: '',
        access_speed_mbps_3: '',
        num_users: '',
        num_ip_phones: '',
        num_vc_devices: '',
        implementation_approach: '',
        ip_address: '',
        pop_name_location: '',
        technical_demarcation: '',
        diversity_requirement: 'None',
        path_diversity: 'Yes',
        dual_building_entry: 'Yes',
        dual_termination_points: 'Yes',
        dual_diverse_pop: 'Yes',
        dual_pe_router: 'Yes',
        dual_loop: 'Yes',
        customer_name: '',
        customer_contact_mail: userRole?.userEmail || '',
        customer_contact_phone: '',
        site_technical_user_email: '',
        site_technical_user_name: '',
        site_technical_user_phone: '',
        lcon_name: '',
        lcon_email: '',
        lcon_phone: '',
    });

    const [custPhoneError, setCustPhoneError] = useState('');
    const [siteTechEmailError, setSiteTechEmailError] = useState('');
    const [siteTechPhoneError, setSiteTechPhoneError] = useState('');

    const [temLoading, setTemLoading] = useState(false);
    const [temData, setTemData] = useState(null);
    const [siteIdOptions, setSiteIdOptions] = useState([]);
    const [siteIdOptionsLoading, setSiteIdOptionsLoading] = useState(false);
    const [availableCircuits, setAvailableCircuits] = useState([]);
    const [selectedCircuits, setSelectedCircuits] = useState([]);
    const [costEstimate, setCostEstimate] = useState(null);
    const [costEstimateLoading, setCostEstimateLoading] = useState(false);
    const isNewSite = localData.is_new_site === 'Yes';

    const [countryCityData, setCountryCityData] = useState({ countries: [], cities: {}, country_names: {}, city_names: {} });
    const [countryCityLoading, setCountryCityLoading] = useState(false);
    const [siteIdGenerating, setSiteIdGenerating] = useState(false);
    const [manualCity, setManualCity] = useState(false);
    const [manualCityText, setManualCityText] = useState(''); // full typed city name; site_city stores only its first 4 letters
    const [locationIdLoading, setLocationIdLoading] = useState(false);
    const [locationIdFound, setLocationIdFound] = useState(null); // null = not looked up yet, true/false = found/not found

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [savedId, setSavedId] = useState(updateId ? Number(updateId) : null);
    const [grmDuplicate, setGrmDuplicate] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    const [successOpen, setSuccessOpen] = useState(false);
    const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

    const odmTitle = watch('odmTitle');
    const grmDemandNumber = watch('grmDemandNumber');

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setLocalData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handlePhoneChange = (name, setError) => (e) => {
        const { value } = e.target;
        setLocalData(prev => ({ ...prev, [name]: value }));
        setError(validatePhone(value) || '');
    };

    const handleEmailChange = (name, setError) => (e) => {
        const { value } = e.target;
        setLocalData(prev => ({ ...prev, [name]: value }));
        setError(validateEmail(value) || '');
    };

    const handleSiteIdOpen = async () => {
        setSiteIdOptionsLoading(true);
        try {
            const res = await ApiService.temSiteIds(localData.site_country, localData.site_city);
            setSiteIdOptions(res.site_ids || []);
        } catch (e) {
            console.error('Failed to load site IDs:', e);
        } finally {
            setSiteIdOptionsLoading(false);
        }
    };

    const handleSiteIdSelect = async (siteId) => {
        setLocalData(prev => ({ ...prev, customer_site_id: siteId || '', install_site_address: '', existing_circuit_ids: '', location_id: '' }));
        setTemData(null);
        setAvailableCircuits([]);
        setSelectedCircuits([]);
        setLocationIdFound(null);
        if (!siteId) return;
        setTemLoading(true);
        setLocationIdLoading(true);
        ApiService.getLocationId(siteId)
            .then(res => {
                setLocalData(prev => ({ ...prev, location_id: res.found ? res.location_id : '' }));
                setLocationIdFound(!!res.found);
            })
            .catch(() => setLocationIdFound(false))
            .finally(() => setLocationIdLoading(false));
        try {
            const result = await ApiService.temLookup(siteId);
            if (result.found && result.records && result.records.length > 0) {
                const first = result.records[0];
                const circuits = result.records.map(r => r.circuit_id_provider || r.cir_id).filter(Boolean);
                setTemData({ found: true, records: result.records });
                setAvailableCircuits(circuits);
                setLocalData(prev => ({ ...prev, customer_site_id: siteId, install_site_address: first.site_address || '' }));
            } else {
                setTemData({ found: false });
            }
        } catch (e) {
            setTemData({ found: false, error: e.message });
        } finally {
            setTemLoading(false);
        }
    };

    useEffect(() => {
        setLocalData(prev => ({ ...prev, existing_circuit_ids: selectedCircuits.join(', ') }));
    }, [selectedCircuits]);

    // Pre-fill GRM-provided fields when arriving via GRM redirect
    useEffect(() => {
        if (!externalDemandData) return;
        setLocalData(prev => ({
            ...prev,
            cost_centre: externalDemandData.cost_centre || prev.cost_centre,
            debtor: externalDemandData.debtor || prev.debtor,
            oe_name: externalDemandData.oe_name || prev.oe_name,
            request_description: externalDemandData.description_of_odm_request || prev.request_description,
        }));
    }, [externalDemandData]);

    // Auto-calculate bandwidth from user/phone/VC counts
    useEffect(() => {
        const users = parseFloat(localData.num_users) || 0;
        const phones = parseFloat(localData.num_ip_phones) || 0;
        const vc = parseFloat(localData.num_vc_devices) || 0;
        if (!users && !phones && !vc) return;
        const raw = users * 2 + phones * 0.064 + vc * 3;
        const withBuffer = raw * 1.1;
        setLocalData(prev => ({ ...prev, access_speed_mbps: Math.ceil(withBuffer) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localData.num_users, localData.num_ip_phones, localData.num_vc_devices]);

    // Fetch cost estimate when site + bandwidth are known
    useEffect(() => {
        const siteId = localData.customer_site_id;
        const bw = parseFloat(localData.access_speed_mbps);
        const bw2 = parseFloat(localData.access_speed_mbps_2) || undefined;
        const bw3 = parseFloat(localData.access_speed_mbps_3) || undefined;
        const country = localData.site_country;
        // New site: need country + bandwidth. Existing site: need site_id + bandwidth.
        const canFetch = isNewSite ? (bw && country) : (bw && siteId);
        if (!canFetch) {
            setCostEstimate(null);
            return;
        }
        let cancelled = false;
        setCostEstimateLoading(true);
        ApiService.circuitCostEstimate(siteId, bw, country, isNewSite, bw2, bw3, localData.site_type)
            .then(res => { if (!cancelled) setCostEstimate(res); })
            .catch(() => { if (!cancelled) setCostEstimate(null); })
            .finally(() => { if (!cancelled) setCostEstimateLoading(false); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localData.customer_site_id, localData.access_speed_mbps, localData.access_speed_mbps_2, localData.access_speed_mbps_3, localData.site_country, localData.site_type, isNewSite]);

    // Load country/city list from TEM on mount
    useEffect(() => {
        const load = async () => {
            setCountryCityLoading(true);
            try {
                const res = await ApiService.temCountriesCities();
                setCountryCityData({
                    countries: res.countries || [],
                    cities: res.cities || {},
                    country_names: res.country_names || {},
                    city_names: res.city_names || {},
                });
            } catch (e) {
                console.error('Failed to load countries/cities:', e);
            } finally {
                setCountryCityLoading(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (isNewSite || !manualCity) return;
        setManualCity(false);
        setManualCityText('');
        setLocalData(prev => ({ ...prev, site_city: '', customer_site_id: '' }));
        setSiteIdOptions([]);
        setCostEstimate(null);
    }, [isNewSite]);

    // Auto-generate Site ID when new site + country + city are all set
    useEffect(() => {
        if (!isNewSite || !localData.site_country || !localData.site_city) {
            if (isNewSite) {
                setLocalData(prev => ({ ...prev, customer_site_id: '' }));
            }
            return;
        }
        const generate = async () => {
            setSiteIdGenerating(true);
            try {
                const res = await ApiService.generateSiteId(localData.site_country, localData.site_city);
                setLocalData(prev => ({ ...prev, customer_site_id: res.site_id || '' }));
            } catch (e) {
                console.error('Failed to generate site ID:', e);
            } finally {
                setSiteIdGenerating(false);
            }
        };
        generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNewSite, localData.site_country, localData.site_city]);

    const handleGrmCheck = async () => {
        if (!grmDemandNumber) return;
        try {
            const res = await ApiService.get(`/api/circuit-orders/check-grm-number/${grmDemandNumber}`);
            setGrmDuplicate(!!res.exists);
        } catch (_) {}
    };

    const _toFloat = v => { const n = parseFloat(v); return isNaN(n) ? null : n; };
    const _toInt   = v => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };

    const buildPayload = () => ({
        ...localData,
        odm_title: odmTitle || '',
        grm_demand_number: grmDemandNumber || '',
        oe_name: localData.oe_name || userName,
        request_type: 'circuit_ordering',
        port_speed_mbps:   _toFloat(localData.port_speed_mbps),
        access_speed_mbps: _toFloat(localData.access_speed_mbps),
        access_speed_mbps_2: _toFloat(localData.access_speed_mbps_2),
        access_speed_mbps_3: _toFloat(localData.access_speed_mbps_3),
        num_users:         _toInt(localData.num_users),
        num_ip_phones:     _toInt(localData.num_ip_phones),
        num_vc_devices:    _toInt(localData.num_vc_devices),
    });

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = buildPayload();
            if (isUpdateMode && updateId) {
                await ApiService.updateCircuitOrder(updateId, payload);
                setDialogTitle('Saved');
                setDialogMessage('Circuit order draft updated successfully.');
            } else {
                const result = await ApiService.createCircuitOrder(payload);
                setSavedId(result.id);
                setDialogTitle('Saved');
                setDialogMessage('Circuit order saved as draft.');
            }
            ApiService.invalidateRequestsCache();
            setDialogOpen(true);
        } catch (e) {
            setDialogTitle('Error');
            setDialogMessage('Failed to save draft: ' + e.message);
            setDialogOpen(true);
        } finally {
            setIsSaving(false);
        }
    };

    const handleFormSubmit = async () => {
        const missing = [];
        if (!localData.site_country) missing.push('Country');
        if (!localData.site_city) missing.push('City');
        if (isNewSite && !localData.customer_site_id) missing.push('Customer Site ID (still generating, please wait)');
        if (isNewSite && !localData.location_id) missing.push('Primary Location ID');
        if (!localData.product) missing.push('Product');
        if (!localData.region) missing.push('Region');
        if (!localData.request_description) missing.push('Request Description');
        if (!localData.customer_name) missing.push('Customer Name');
        if (!localData.customer_contact_phone) missing.push('Customer Contact Phone');
        if (!localData.site_technical_user_email) missing.push('Site Technical User Email');
        if (!localData.site_technical_user_name) missing.push('Site Technical User Name');
        if (!localData.site_technical_user_phone) missing.push('Site Technical User Phone');
        if (missing.length > 0) {
            setDialogTitle('Validation Error');
            setDialogMessage(`Please fill in all required fields: ${missing.join(', ')}.`);
            setDialogOpen(true);
            return;
        }
        const phoneErr = validatePhone(localData.customer_contact_phone) || validatePhone(localData.site_technical_user_phone);
        if (phoneErr) {
            setDialogTitle('Validation Error');
            setDialogMessage('Please correct phone number format: +XX (XXX) XXX-XXXX');
            setDialogOpen(true);
            return;
        }
        if (validateEmail(localData.site_technical_user_email)) {
            setDialogTitle('Validation Error');
            setDialogMessage('Please enter a valid Site Technical User Email.');
            setDialogOpen(true);
            return;
        }
        if (validateRequiredDate(localData.customer_required_date)) {
            setDialogTitle('Validation Error');
            setDialogMessage('Customer Required Date must be at least 3 months from today.');
            setDialogOpen(true);
            return;
        }
        if (grmDuplicate) {
            setDialogTitle('Validation Error');
            setDialogMessage('This GRM Demand Number already exists. Please check with your team.');
            setDialogOpen(true);
            return;
        }
        setConfirmSubmitOpen(true);
        // If we don't have a cost estimate yet (site/BW not set), fetch one now
        if (!costEstimate && localData.customer_site_id && localData.access_speed_mbps) {
            setCostEstimateLoading(true);
            ApiService.circuitCostEstimate(localData.customer_site_id, localData.access_speed_mbps, localData.site_country, false, localData.access_speed_mbps_2, localData.access_speed_mbps_3, localData.site_type)
                .then(res => setCostEstimate(res))
                .catch(() => {})
                .finally(() => setCostEstimateLoading(false));
        }
    };

    const handleConfirmedSubmit = async () => {
        setConfirmSubmitOpen(false);
        setIsSubmitting(true);
        try {
            let id = savedId;
            if (!id) {
                const payload = buildPayload();
                const created = await ApiService.createCircuitOrder(payload);
                id = created.id;
                setSavedId(id);
            }
            await ApiService.submitCircuitOrder(id, costEstimate || null);
            ApiService.invalidateRequestsCache();
            setSuccessOpen(true);
        } catch (e) {
            setDialogTitle('Error');
            setDialogMessage('Submission failed: ' + e.message);
            setDialogOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        if (dialogTitle === 'Saved' || dialogTitle === 'Success') {
            navigate('/track-requests');
        }
    };

    useEffect(() => {
        if (!isUpdateMode || !updateId) return;
        const load = async () => {
            try {
                const data = await ApiService.getCircuitOrder(updateId);
                setLocalData(prev => ({ ...prev, ...data }));
            } catch (e) {
                console.error('Error loading circuit order:', e);
            }
        };
        load();
    }, [isUpdateMode, updateId]);


    useEffect(() => {
        if (!isUpdateMode || !localData.site_country || !localData.site_city) return;
        if (countryCityLoading) return;
        const knownCities = countryCityData.cities[localData.site_country] || [];
        if (!knownCities.includes(localData.site_city)) {
            setManualCity(true);
            setManualCityText(localData.site_city);
        }
    }, [isUpdateMode, localData.site_country, localData.site_city, countryCityLoading]);

    useEffect(() => {
        if (userRole?.userEmail) {
            setLocalData(prev => ({ ...prev, customer_contact_mail: userRole.userEmail }));
        }
    }, [userRole?.userEmail]);

    // ── Field builders — always render helperText so rows stay the same height ──

    const tf = (name, label, opts = {}) => (
        <TextField
            fullWidth
            variant="outlined"
            size="medium"
            label={opts.required ? `${label} *` : label}
            name={name}
            value={localData[name] ?? ''}
            onChange={handleChange}
            multiline={opts.multiline || false}
            rows={opts.rows}
            type={opts.type || 'text'}
            error={!!opts.error}
            helperText={opts.error || opts.helperText || ' '}
            placeholder={opts.placeholder}
            InputLabelProps={{ shrink: true }}
            disabled={opts.disabled || false}
            InputProps={opts.readOnly ? { readOnly: true } : undefined}
            inputProps={opts.inputProps}
            sx={{
                ...(opts.disabled ? { '& .Mui-disabled': { backgroundColor: '#f5f5f5', WebkitTextFillColor: 'rgba(0,0,0,0.7)' } } : {}),
                ...FIELD_SX,
            }}
        />
    );

    const sf = (name, label, options, opts = {}) => {
        const labelText = opts.required ? `${label} *` : label;
        return (
            <FormControl fullWidth variant="outlined" size="medium" sx={FIELD_SX}>
                <InputLabel shrink sx={{ backgroundColor: '#fff', px: 0.5 }}>
                    {labelText}
                </InputLabel>
                <Select
                    name={name}
                    value={localData[name] ?? ''}
                    onChange={handleChange}
                    label={labelText}
                    notched
                    displayEmpty
                >
                    {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
                <FormHelperText>{opts.helperText || ' '}</FormHelperText>
            </FormControl>
        );
    };

    return (
        <>
            <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Location ID required:</strong> A valid Location ID (SecID) is mandatory to submit this request.
                If the site doesn't have one yet, create it in ServiceNow (Self-Service &gt; Service Catalog &gt; SIM Services catalogue) before proceeding.
            </Alert>

            {grmDuplicate && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    GRM Demand Number already exists in the system.
                </Alert>
            )}

            {/* ── Section 1: Site & Installation + Core Information (merged) ──── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Site &amp; Installation
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Row 1: OE / BU Name + Is it a New Site? + Site Type */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            {tf('oe_name', 'OE / BU Name', { required: true })}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            {sf('is_new_site', 'Is it a New Site?', ['Yes', 'No'])}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            {sf('site_type', 'Site Type', SITE_TYPE_OPTIONS, { helperText: 'Affects SD-WAN Management cost' })}
                        </Box>
                    </Box>

                    {/* Row 1b: Country + City (always visible) */}
                    <Box sx={{ display: 'flex', gap: 2 }}>

                        {/* ── COUNTRY ── */}
                        <Box sx={{ flex: 1 }}>
                            <FormControl fullWidth variant="outlined" size="medium" sx={FIELD_SX}>
                                <InputLabel shrink sx={{ backgroundColor: '#fff', px: 0.5 }}>
                                    Country *
                                </InputLabel>
                                <Select
                                    name="site_country"
                                    value={localData.site_country || ''}
                                    onChange={(e) => {
                                        const newCountry = e.target.value;
                                        const mappedRegion = COUNTRY_TO_REGION[newCountry];
                                        setLocalData(prev => ({
                                            ...prev,
                                            site_country: newCountry,
                                            site_city: '',
                                            customer_site_id: '',
                                            ...(mappedRegion ? { region: mappedRegion } : {}),
                                        }));
                                        setSiteIdOptions([]);
                                        setCostEstimate(null);
                                        setManualCity(false);
                                        setManualCityText('');
                                    }}
                                    label="Country *"
                                    notched
                                    displayEmpty
                                    disabled={countryCityLoading}
                                    MenuProps={{
                                        PaperProps: { style: { maxHeight: 240 } },
                                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                                    }}
                                >
                                    {countryCityData.countries.map(c => {
                                        const fullName = countryCityData.country_names[c];
                                        return (
                                            <MenuItem key={c} value={c}>
                                                {fullName ? `${fullName} (${c})` : c}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                <FormHelperText>{countryCityLoading ? 'Loading...' : ' '}</FormHelperText>
                            </FormControl>
                        </Box>

                        {/* ── CITY ── */}
                        <Box sx={{ flex: 1 }}>
                            {manualCity ? (
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="medium"
                                    label="City *"
                                    name="site_city"
                                    value={manualCityText}
                                    onChange={(e) => {
                                        const typed = e.target.value;
                                        setManualCityText(typed);
                                        const code = typed.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 4);
                                        setLocalData(prev => (
                                            prev.site_city === code ? prev : { ...prev, site_city: code, customer_site_id: '' }
                                        ));
                                        if (code !== localData.site_city) {
                                            setSiteIdOptions([]);
                                            setCostEstimate(null);
                                        }
                                    }}
                                    disabled={!localData.site_country}
                                    InputLabelProps={{ shrink: true }}
                                    helperText={localData.site_city ? `Site ID code: ${localData.site_city}` : 'Enter the city name'}
                                    sx={FIELD_SX}
                                />
                            ) : (
                                <FormControl fullWidth variant="outlined" size="medium" sx={FIELD_SX}>
                                    <InputLabel shrink sx={{ backgroundColor: '#fff', px: 0.5 }}>
                                        City *
                                    </InputLabel>
                                    <Select
                                        name="site_city"
                                        value={localData.site_city || ''}
                                        onChange={(e) => {
                                            setLocalData(prev => ({ ...prev, site_city: e.target.value, customer_site_id: '' }));
                                            setSiteIdOptions([]);
                                            setCostEstimate(null);
                                        }}
                                        label="City *"
                                        notched
                                        displayEmpty
                                        disabled={!localData.site_country}
                                        MenuProps={{
                                            PaperProps: { style: { maxHeight: 240 } },
                                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                                        }}
                                    >
                                        {(countryCityData.cities[localData.site_country] || []).map(c => {
                                            const fullName = (countryCityData.city_names[localData.site_country] || {})[c];
                                            return (
                                                <MenuItem key={c} value={c}>
                                                    {fullName ? `${fullName} (${c})` : c}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                    <FormHelperText>
                                        {!localData.site_country ? 'Select a country first' : ' '}
                                    </FormHelperText>
                                </FormControl>
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={manualCity}
                                        disabled={!localData.site_country || !isNewSite}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setManualCity(checked);
                                            setManualCityText('');
                                            setLocalData(prev => ({ ...prev, site_city: '', customer_site_id: '' }));
                                            setSiteIdOptions([]);
                                            setCostEstimate(null);
                                        }}
                                    />
                                }
                                label="City not listed — enter manually"
                                sx={{ mt: -0.5, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', color: 'text.secondary' } }}
                            />
                        </Box>

                    </Box>

                    {/* Row 2: Customer Site ID + Install Site Address */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            {isNewSite ? (
                                <TextField
                                    fullWidth variant="outlined"
                                    label="Customer Site ID"
                                    name="customer_site_id"
                                    value={localData.customer_site_id}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ readOnly: true }}
                                    helperText={
                                        siteIdGenerating ? 'Generating unique Site ID...' :
                                        localData.customer_site_id ? `✓ Auto-generated: ${localData.customer_site_id}` :
                                        'Select Country and City to auto-generate'
                                    }
                                    FormHelperTextProps={{
                                        sx: {
                                            color: localData.customer_site_id && !siteIdGenerating ? 'success.main' : undefined,
                                            minHeight: '1.25em',
                                        }
                                    }}
                                    sx={{
                                        ...FIELD_SX,
                                        '& .MuiOutlinedInput-root': { backgroundColor: '#f0fdf4' },
                                    }}
                                />
                            ) : (
                                <Autocomplete
                                    options={siteIdOptions}
                                    value={localData.customer_site_id || null}
                                    onOpen={handleSiteIdOpen}
                                    onChange={(_, newValue) => handleSiteIdSelect(newValue)}
                                    loading={siteIdOptionsLoading}
                                    filterOptions={(options, { inputValue }) =>
                                        options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase())).slice(0, 100)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Customer Site ID"
                                            placeholder="Type to search site IDs"
                                            helperText={
                                                temLoading ? 'Loading site details...' :
                                                temData?.found ? `✓ Found ${temData.records.length} circuit(s)` :
                                                temData?.found === false ? 'Site not found in TEM inventory' :
                                                ' '
                                            }
                                            FormHelperTextProps={{
                                                sx: {
                                                    color: temData?.found ? 'success.main' :
                                                           temData?.found === false ? 'error.main' : undefined,
                                                    minHeight: '1.25em',
                                                }
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {siteIdOptionsLoading || temLoading ? <CircularProgress size={18} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    ListboxProps={{ style: { maxHeight: 250 } }}
                                />
                            )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined"
                                label={isNewSite ? 'Install Site Address *' : 'Install Site Address'}
                                name="install_site_address"
                                value={localData.install_site_address}
                                onChange={handleChange}
                                multiline rows={2}
                                InputLabelProps={{ shrink: true }}
                                helperText={
                                    isNewSite
                                        ? 'Enter the full address for the new site'
                                        : 'Auto-filled from TEM lookup (Address a column), or enter manually'
                                }
                                sx={{
                                    ...FIELD_SX,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: !isNewSite && temData?.found ? '#f0fdf4' : undefined,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Row 2b: Location ID (SecID) */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            {isNewSite ? (
                                <TextField
                                    fullWidth variant="outlined"
                                    label={
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            Primary Location ID *
                                            <Tooltip
                                                arrow
                                                placement="right"
                                                title={
                                                    <Box component="span" sx={{ display: 'block' }}>
                                                        To create a Location ID:<br />
                                                        1. Access the ServiceNow platform (logged in via SSO)<br />
                                                        2. Open the service catalogue: Self-Service &gt; Service Catalog<br />
                                                        3. Select the SIM Services catalogue item
                                                    </Box>
                                                }
                                            >
                                                <HelpOutlineIcon
                                                    fontSize="small"
                                                    sx={{
                                                        ml: 0.5,
                                                        color: 'text.secondary',
                                                        fontSize: '16px',
                                                        cursor: 'help',
                                                        '&:hover': { color: 'primary.main' },
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    }
                                    name="location_id"
                                    value={localData.location_id ?? ''}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    error={!localData.location_id}
                                    helperText={!localData.location_id ? 'Required — create via ServiceNow SIM Services catalogue' : ' '}
                                    sx={FIELD_SX}
                                />
                            ) : (
                                <TextField
                                    fullWidth variant="outlined"
                                    label="Location ID"
                                    name="location_id"
                                    value={localData.location_id ?? ''}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: locationIdLoading ? <CircularProgress size={18} /> : null,
                                    }}
                                    helperText={
                                        locationIdLoading ? 'Looking up Location ID...' :
                                        locationIdFound === true ? '✓ Auto-populated from Site ID mapping' :
                                        locationIdFound === false ? 'No Location ID mapping found for this Site ID' :
                                        'Auto-populated when a Site ID is selected'
                                    }
                                    FormHelperTextProps={{
                                        sx: {
                                            color: locationIdFound === true ? 'success.main' :
                                                   locationIdFound === false ? 'text.secondary' : undefined,
                                            minHeight: '1.25em',
                                        }
                                    }}
                                    sx={{
                                        ...FIELD_SX,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: locationIdFound === true ? '#f0fdf4' : undefined,
                                        },
                                    }}
                                />
                            )}
                        </Box>
                        <Box sx={{ flex: 1 }} />
                    </Box>

                    {/* Row 3: Demarcation + Dual Building Entry */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            {tf('demarcation_location', 'Demarcation / Handover Location')}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            {sf('dual_building_entry_availability', 'Dual Building Entry at Site', YES_NO)}
                        </Box>
                    </Box>

                    {/* Row 4: Cost Centre + Debtor */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            {tf('cost_centre', 'Cost Centre', { required: true })}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            {tf('debtor', 'Debtor', { required: true })}
                        </Box>
                    </Box>

                    {/* Row 5: Existing Circuit IDs (new site) or Available Circuits (existing site) */}
                    {isNewSite ? (
                        <Box>
                            <TextField
                                fullWidth variant="outlined"
                                label="Existing Circuit IDs (for Change Requests)"
                                name="existing_circuit_ids"
                                value={localData.existing_circuit_ids}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                helperText="Enter existing circuit IDs separated by commas if applicable"
                                sx={FIELD_SX}
                            />
                        </Box>
                    ) : availableCircuits.length > 0 && (
                        <Box>
                            <CircuitSelection
                                circuits={availableCircuits}
                                selectedCircuits={selectedCircuits}
                                onChange={setSelectedCircuits}
                            />
                        </Box>
                    )}

                </Box>
            </Paper>

            {/* ── Section 3: Request Details ────────────────────────────────────── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Request Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>{sf('product', 'Product', PRODUCT_OPTIONS, { required: true })}</Box>
                        <Box sx={{ flex: 1 }}>{sf('region', 'Region', REGION_OPTIONS, { required: true })}</Box>
                        <Box sx={{ flex: 1 }}>{tf('customer_required_date', 'Customer Required Date', {
                            type: 'date',
                            inputProps: { min: getMinRequiredDate() },
                            error: validateRequiredDate(localData.customer_required_date),
                            helperText: 'Must be at least 3 months from today',
                        })}</Box>
                    </Box>
                </Box>
            </Paper>

            {/* ── Section 4: Request Description ───────────────────────────────── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Request Description
                </Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Description of ODM Request *"
                    name="request_description"
                    value={localData.request_description}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    InputLabelProps={{ shrink: true }}
                    error={!localData.request_description}
                    helperText={!localData.request_description ? 'Description is required' : ' '}
                    sx={FIELD_SX}
                />
            </Paper>

            {/* ── Section 5: Business Reasons ──────────────────────────────────── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Business Reasons
                </Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Business Reasons"
                    name="business_reasons"
                    value={localData.business_reasons}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    InputLabelProps={{ shrink: true }}
                    helperText=" "
                    sx={FIELD_SX}
                />
            </Paper>

            {/* ── Section 6: Technical Information ─────────────────────────────── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Technical Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Row 1: Circuit ID(s) */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>{tf('circuit_id', 'Circuit ID (at Implementation)')}</Box>
                        <Box sx={{ flex: 1 }}>
                            {isNewSite
                                ? tf('circuit_id_2', 'Circuit ID 2 (at Implementation)')
                                : null}
                        </Box>
                        <Box sx={{ flex: 1 }} />
                    </Box>

                    <Divider />
                    <Typography variant="subtitle2" sx={{ color: '#003781' }}>
                        Diversity Requirements
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>{sf('diversity_requirement', 'Diversity Requirement', DIVERSITY_OPTIONS)}</Box>
                        <Box sx={{ flex: 2 }} />
                    </Box>

                    <Divider />
                    <Typography variant="subtitle2" sx={{ color: '#003781' }}>
                        Bandwidth Estimation
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>{tf('num_users', 'Number of Users (AMC / Fat Client)', { type: 'number', helperText: '2 Mbps per user' })}</Box>
                        <Box sx={{ flex: 1 }}>{tf('num_ip_phones', 'Number of IP Phones', { type: 'number', helperText: '64 Kbps per phone' })}</Box>
                        <Box sx={{ flex: 1 }}>{tf('num_vc_devices', 'Number of VC Devices', { type: 'number', helperText: '3 Mbps per device' })}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="medium"
                                label="Access Speed (Mbps) *"
                                name="access_speed_mbps"
                                value={localData.access_speed_mbps ?? ''}
                                onChange={handleChange}
                                type="number"
                                InputLabelProps={{ shrink: true }}
                                helperText={
                                    (localData.num_users || localData.num_ip_phones || localData.num_vc_devices)
                                        ? `Auto-calculated incl. 10% buffer — edit to override`
                                        : 'Enter required bandwidth or fill counts above to auto-calculate'
                                }
                                sx={{
                                    ...FIELD_SX,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: (localData.num_users || localData.num_ip_phones || localData.num_vc_devices) ? '#f0fdf4' : undefined,
                                    },
                                }}
                            />
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="medium"
                                label="Access Speed 2 (Mbps)"
                                name="access_speed_mbps_2"
                                value={localData.access_speed_mbps_2 ?? ''}
                                onChange={handleChange}
                                type="number"
                                InputLabelProps={{ shrink: true }}
                                helperText="Optional — add a 2nd circuit"
                                sx={FIELD_SX}
                            />
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="medium"
                                label="Access Speed 3 (Mbps)"
                                name="access_speed_mbps_3"
                                value={localData.access_speed_mbps_3 ?? ''}
                                onChange={handleChange}
                                type="number"
                                InputLabelProps={{ shrink: true }}
                                helperText="Optional — add a 3rd circuit"
                                sx={FIELD_SX}
                            />
                        </Box>
                        <Box sx={{ flex: 2 }}>
                            {costEstimateLoading && (
                                <Alert severity="info" sx={{ mt: 0.5 }} icon={<CircularProgress size={16} />}>
                                    Looking up TEM pricing…
                                </Alert>
                            )}
                            {!costEstimateLoading && costEstimate?.estimates?.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    {costEstimate.estimates.map((e, i) => (
                                        <Box key={i} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Cost Estimate
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ ml: 'auto', fontWeight: 700 }}>
                                                    Total: €{e.total?.toFixed(2)}
                                                </Typography>
                                            </Box>

                                            <Accordion disableGutters defaultExpanded elevation={0} sx={{ border: '1px solid #f0f0f0' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0 } }}>
                                                    <Typography variant="body2">View Cost Breakdown</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 1 }}>
                                                    <TableContainer>
                                                        <Table size="small">
                                                            <TableBody>
                                                                <TableRow>
                                                                    <TableCell>SD-WAN Circuit(s) (908138)</TableCell>
                                                                    <TableCell align="right">€{e.breakdown?.sdWanCircuits?.toFixed(2) ?? '0.00'}</TableCell>
                                                                </TableRow>
                                                                <TableRow>
                                                                    <TableCell>SD-WAN Management (908134)</TableCell>
                                                                    <TableCell align="right">€{e.breakdown?.sdWanManagement?.toFixed(2) ?? '0.00'}</TableCell>
                                                                </TableRow>
                                                                <TableRow>
                                                                    <TableCell>SD-WAN Router (908137)</TableCell>
                                                                    <TableCell align="right">€{e.breakdown?.sdWanRouter?.toFixed(2) ?? '0.00'}</TableCell>
                                                                </TableRow>
                                                                <TableRow>
                                                                    <TableCell>SD-WAN IMACD — OTC Provider (908374)</TableCell>
                                                                    <TableCell align="right">€{e.breakdown?.sdWanImacd?.toFixed(2) ?? '0.00'}</TableCell>
                                                                </TableRow>
                                                                <TableRow>
                                                                    <TableCell>Integrator Effort — Accenture (905513)</TableCell>
                                                                    <TableCell align="right">€{e.breakdown?.accentureEffort?.toFixed(2) ?? '0.00'}</TableCell>
                                                                </TableRow>
                                                                <TableRow>
                                                                    <TableCell>SD-WAN Effort (2 PD) (908374)</TableCell>
                                                                    <TableCell align="right">€{e.breakdown?.sdWanEffort?.toFixed(2) ?? '0.00'}</TableCell>
                                                                </TableRow>
                                                                <TableRow>
                                                                    <TableCell>ODM Team Effort (905513)</TableCell>
                                                                    <TableCell align="right">€{e.breakdown?.odmTeamEffort?.toFixed(2) ?? '0.00'}</TableCell>
                                                                </TableRow>
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                    <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                                        Numbers in brackets are material numbers. MRC: €{e.mrc?.toFixed(2)} | NRC: €{e.nrc?.toFixed(2)}
                                                    </Typography>
                                                </AccordionDetails>
                                            </Accordion>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {!costEstimateLoading && costEstimate?.found === false && (
                                <Alert severity="info" sx={{ mt: 0.5 }}>
                                    {costEstimate.is_new_site
                                        ? `No TEM records found for country ${localData.site_country} — cost estimate not available.`
                                        : 'Site not in TEM inventory — cost estimate not available.'}
                                </Alert>
                            )}
                            {!costEstimateLoading && !costEstimate && isNewSite && localData.site_country && localData.access_speed_mbps && (
                                <Alert severity="info" sx={{ mt: 0.5 }} icon={<CircularProgress size={16} />}>
                                    Looking up TEM pricing for {localData.site_country}…
                                </Alert>
                            )}
                        </Box>
                    </Box>

                    <Divider />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>{tf('port_speed_mbps', 'Port Speed (Mbps)', { type: 'number' })}</Box>
                        <Box sx={{ flex: 2 }}>{sf('implementation_approach', 'Implementation Approach', IMPLEMENTATION_OPTIONS)}</Box>
                    </Box>

                </Box>
            </Paper>

            {/* ── Section 7: Customer Contact ───────────────────────────────────── */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Customer Contact
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined" size="medium"
                                label="Customer Name *"
                                name="customer_name"
                                value={localData.customer_name}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                error={!localData.customer_name}
                                helperText={!localData.customer_name ? 'Required' : ' '}
                                sx={FIELD_SX}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined" size="medium"
                                label="Requestor Email *"
                                name="customer_contact_mail"
                                value={localData.customer_contact_mail}
                                type="email"
                                disabled
                                InputProps={{ readOnly: true }}
                                InputLabelProps={{ shrink: true }}
                                helperText=" "
                                sx={{
                                    ...FIELD_SX,
                                    '& .Mui-disabled': { backgroundColor: '#f5f5f5', WebkitTextFillColor: 'rgba(0,0,0,0.7)' },
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined" size="medium"
                                label="Customer Contact Phone *"
                                name="customer_contact_phone"
                                value={localData.customer_contact_phone}
                                onChange={handlePhoneChange('customer_contact_phone', setCustPhoneError)}
                                placeholder="+XX (XXX) XXX-XXXX"
                                InputLabelProps={{ shrink: true }}
                                error={!!custPhoneError}
                                helperText={custPhoneError || 'Format: +XX (XXX) XXX-XXXX'}
                                sx={FIELD_SX}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined" size="medium"
                                label="Site Technical User Name *"
                                name="site_technical_user_name"
                                value={localData.site_technical_user_name}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                error={!localData.site_technical_user_name}
                                helperText={!localData.site_technical_user_name ? 'Required' : ' '}
                                sx={FIELD_SX}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined" size="medium"
                                label="Site Technical User Email *"
                                name="site_technical_user_email"
                                value={localData.site_technical_user_email}
                                onChange={handleEmailChange('site_technical_user_email', setSiteTechEmailError)}
                                type="email"
                                InputLabelProps={{ shrink: true }}
                                error={!!siteTechEmailError}
                                helperText={siteTechEmailError || ' '}
                                sx={FIELD_SX}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined" size="medium"
                                label="Site Technical User Phone *"
                                name="site_technical_user_phone"
                                value={localData.site_technical_user_phone}
                                onChange={handlePhoneChange('site_technical_user_phone', setSiteTechPhoneError)}
                                placeholder="+XX (XXX) XXX-XXXX"
                                InputLabelProps={{ shrink: true }}
                                error={!!siteTechPhoneError}
                                helperText={siteTechPhoneError || 'Format: +XX (XXX) XXX-XXXX'}
                                sx={FIELD_SX}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined" size="medium"
                                label="LCON Name"
                                name="lcon_name"
                                value={localData.lcon_name ?? ''}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                helperText=" "
                                sx={FIELD_SX}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined" size="medium"
                                label="LCON Email"
                                name="lcon_email"
                                value={localData.lcon_email ?? ''}
                                onChange={handleChange}
                                type="email"
                                InputLabelProps={{ shrink: true }}
                                helperText=" "
                                sx={FIELD_SX}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                fullWidth variant="outlined" size="medium"
                                label="LCON Phone"
                                name="lcon_phone"
                                value={localData.lcon_phone ?? ''}
                                onChange={handleChange}
                                placeholder="+XX (XXX) XXX-XXXX"
                                InputLabelProps={{ shrink: true }}
                                helperText="Format: +XX (XXX) XXX-XXXX"
                                sx={FIELD_SX}
                            />
                        </Box>
                    </Box>
                </Box>
            </Paper>


            {/* ── Action Buttons ────────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isSubmitting || isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} /> : null}
                >
                    {isSaving ? 'Saving...' : 'Save as Draft'}
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate('/track-requests')}
                    disabled={isSubmitting || isSaving}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFormSubmit}
                    disabled={isSubmitting || isSaving}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
            </Box>

            {/* Info / Error Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle sx={{ color: dialogTitle === 'Error' || dialogTitle === 'Validation Error' ? 'error.main' : '#003781' }}>
                    {dialogTitle}
                </DialogTitle>
                <DialogContent>
                    <Typography>{dialogMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} variant="contained" sx={{ bgcolor: '#003781' }}>OK</Button>
                </DialogActions>
            </Dialog>

            {/* Cost Estimate / Confirm Submit Dialog */}
            <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Typography variant="h6">
                        {costEstimate?.estimates?.length > 0 ? 'Cost Estimate — Circuit Order' : 'Confirm Submission'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {costEstimateLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2">Calculating cost estimate…</Typography>
                        </Box>
                    )}

                    {!costEstimateLoading && costEstimate?.estimates?.length > 0 ? (
                        <>
                            {costEstimate.estimates.map((e, i) => (
                                <Box key={i} sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
                                        Cost Breakdown
                                    </Typography>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Total Estimated Cost: <strong>€{e.total?.toFixed(2)}</strong>
                                    </Typography>

                                    <Accordion disableGutters defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="body2">View Cost Breakdown</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell>SD-WAN Circuit(s) (908138)</TableCell>
                                                            <TableCell align="right">€{e.breakdown?.sdWanCircuits?.toFixed(2) ?? '0.00'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>SD-WAN Management (908134)</TableCell>
                                                            <TableCell align="right">€{e.breakdown?.sdWanManagement?.toFixed(2) ?? '0.00'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>SD-WAN Router (908137)</TableCell>
                                                            <TableCell align="right">€{e.breakdown?.sdWanRouter?.toFixed(2) ?? '0.00'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>SD-WAN IMACD — OTC Provider (908374)</TableCell>
                                                            <TableCell align="right">€{e.breakdown?.sdWanImacd?.toFixed(2) ?? '0.00'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>Integrator Effort — Accenture (905513)</TableCell>
                                                            <TableCell align="right">€{e.breakdown?.accentureEffort?.toFixed(2) ?? '0.00'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>SD-WAN Effort (2 PD) (908374)</TableCell>
                                                            <TableCell align="right">€{e.breakdown?.sdWanEffort?.toFixed(2) ?? '0.00'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>ODM Team Effort (905513)</TableCell>
                                                            <TableCell align="right">€{e.breakdown?.odmTeamEffort?.toFixed(2) ?? '0.00'}</TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                                Numbers in brackets are material numbers. MRC: €{e.mrc?.toFixed(2)} | NRC: €{e.nrc?.toFixed(2)}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                            ))}
                        </>
                    ) : !costEstimateLoading && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                                No TEM Pricing Available
                            </Typography>
                            <Typography variant="body2">
                                A cost estimate could not be generated from TEM data for this site and bandwidth.
                                Once submitted, your request will be sent to vendors for quotation.
                                You will receive vendor quotes for review and approval within 2 working weeks.
                            </Typography>
                        </Alert>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Do you want to proceed with submitting this circuit order?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmSubmitOpen(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleConfirmedSubmit} variant="contained" sx={{ bgcolor: '#003781' }}
                        disabled={isSubmitting}>
                        {isSubmitting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                        Approve &amp; Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Dialog */}
            <Dialog open={successOpen} onClose={() => { setSuccessOpen(false); navigate('/track-requests'); }}>
                <DialogTitle sx={{ color: '#003781' }}>
                    <CheckCircleOutlineIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                    Request Submitted Successfully
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Your circuit order has been submitted. An Amelia (ACAI) workflow ticket has been
                        created and the QUOTE request will be sent to vendors automatically.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        You can track progress in <strong>Track Requests</strong> under the
                        <strong> Circuit Ordering</strong> tab.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained"
                        onClick={() => { setSuccessOpen(false); navigate('/track-requests'); }}
                        sx={{ bgcolor: '#003781' }}>
                        Go to Track Requests
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CircuitOrderForm;
